/* ============================================================
   Lost Collective · Article extras
   File: assets/lc-article-extras.js

   Three small enrichments that all run after the body has been
   rebuilt by lc-article-postprocess:
     1. Contact-sheet fallback — builds the same .lc-contact sheet
        from lco.photographs when the v2 Liquid .lc-contact section is
        absent AND lco.photographs holds 6+ non-scrolly frames.
     2. Read-next corner badges — copies the article tag onto
        each .read-next .pane .img as a corner-badge.
     3. Pull-quote injection — reads JSON from
        #articleBody[data-pull-quotes] and inserts .pull blocks
        between named chapter h2s.

   Waits on window.LCArticle.onReady so chapter h2s and figures
   exist before any of the three look them up.
   ============================================================ */

(function () {
  'use strict';

  window.LCArticle = window.LCArticle || (function () {
    var state = { ready: false, queue: [] };
    return {
      onReady: function (fn) { if (state.ready) fn(); else state.queue.push(fn); },
      fireReady: function () {
        if (state.ready) return;
        state.ready = true;
        state.queue.forEach(function (fn) { try { fn(); } catch (_e) {} });
        state.queue.length = 0;
      }
    };
  })();

  function ready(fn) {
    if (document.readyState !== 'loading') return fn();
    document.addEventListener('DOMContentLoaded', fn);
  }

  function pad(n) { return n < 10 ? '0' + n : '' + n; }

  function el(tag, attrs, html) {
    var n = document.createElement(tag);
    if (attrs) Object.keys(attrs).forEach(function (k) {
      if (k === 'class') n.className = attrs[k];
      else n.setAttribute(k, attrs[k]);
    });
    if (html != null) n.innerHTML = html;
    return n;
  }

  // ----- Contact sheet fallback ---------------------------------------------
  // LOS7-3032. An article without lco.frames gets no Liquid .lc-contact, so
  // this builds the same sheet from lco.photographs (window.LCArticle
  // .photographs, parsed by lc-article-postprocess.js): identical markup, one
  // design, one stylesheet. The old fallback wrote a .photo-index whose
  // numbers (.n) matched no CSS (only .fno was styled), square-cropped every
  // frame, never linked a cell (it read a print_url the payload never
  // carries) and mixed the archive plates in with the film frames.
  //
  // Two runs: "Photographs", then "Archive" (a frame with a credit, no film
  // field, and no print). Every cell is the same square, and the CSS fits the
  // frame inside it by its long edge.
  function contactFrames() {
    var ph = (window.LCArticle && window.LCArticle.photographs) || [];
    var list = Array.isArray(ph) ? ph : Object.keys(ph).sort(function (a, b) {
      return Number(a) - Number(b);
    }).map(function (k) { return ph[k]; });
    return list.filter(function (f) {
      return f && f.cdn_url && !f.scrollytelling && f.width && f.height;
    });
  }

  function isArchiveFrame(f) {
    return !!(f.credit && !f.film && !f.is_print);
  }

  // Thumbnails at the cell size, not the figure's rendition (LOS7-2943). Every
  // frame's long edge is the cell's side, so the long edge is requested at
  // twice the cell, rounded up to a 100px step the CDN caches well: a
  // landscape by its width, a portrait by its height. The old request was a
  // width of aspect x 264, sized for a 132px row, which gave a portrait a
  // third of the pixels a landscape got.
  function thumbEdge(cellPx) {
    return Math.min(1000, Math.max(200, Math.ceil((cellPx * 2) / 100) * 100));
  }

  function thumbUrl(u, ar, edge) {
    if (!u) return u;
    if (u.indexOf('/cdn/shop/') === -1 && u.indexOf('cdn.shopify.com') === -1) return u;
    var key = ar < 1 ? 'height' : 'width';
    // Rebuilt from its parts rather than stripped by a regex: a width= and a
    // height= side by side share one '&', and a single global replace then
    // misses the second, leaving two size params on the request.
    var q = u.indexOf('?');
    var params = q === -1 ? [] : u.slice(q + 1).split('&').filter(function (p) {
      return p && !/^(?:width|height)=/.test(p);
    });
    params.push(key + '=' + edge);
    return (q === -1 ? u : u.slice(0, q)) + '?' + params.join('&');
  }

  function buildContactSheet(root) {
    if (document.querySelector('.lc-contact')) return;
    var frames = contactFrames();
    if (frames.length < 6) return;

    var sheet = el('section', { class: 'lc-contact', 'data-header-mode': 'paper' });
    var hd = el('div', { class: 'hd' });
    hd.appendChild(el('span', { class: 'num' }, '§ Index'));
    hd.appendChild(el('span', { class: 'ttl' }, 'Photograph index · ' + frames.length + ' frames'));
    hd.appendChild(el('span', { class: 'rule' }));
    hd.appendChild(el('span', { class: 'lbl' }, 'Contact sheet'));
    sheet.appendChild(hd);

    // src is assigned AFTER the sheet is in the document (LOS7-2943): set
    // while detached, lazy is not honoured and every thumbnail at the foot of
    // the article was fetched on load.
    var pending = [];
    var runs = [
      { label: 'Photographs', frames: frames.filter(function (f) { return !isArchiveFrame(f); }) },
      { label: 'Archive', frames: frames.filter(isArchiveFrame) },
    ];
    runs.forEach(function (run) {
      if (!run.frames.length) return;
      var group = el('div', {
        class: 'lc-contact__group' + (run.label === 'Archive' ? ' lc-contact__group--archive' : '')
      });
      group.appendChild(el('p', { class: 'lc-contact__sub' }, run.label));
      var grid = el('div', { class: 'grid' });
      run.frames.forEach(function (f) {
        var ar = f.width / f.height;
        var isPrint = !!(f.is_print && f.product_handle);
        var cell = isPrint
          ? el('a', { class: 'cell', href: '/products/' + f.product_handle })
          : el('span', { class: 'cell' });
        var frame = el('span', { class: 'lc-contact__frame' });
        var img = document.createElement('img');
        img.alt = f.title || '';
        img.width = f.width;
        img.height = f.height;
        img.loading = 'lazy';
        img.decoding = 'async';
        frame.appendChild(img);
        cell.appendChild(frame);
        var edge = el('span', { class: 'lc-contact__edge' });
        edge.appendChild(el('span', { class: 'n' }, pad(Number(f.frame_no) || 0)));
        if (f.title) {
          var cap = el('span', { class: 'cap' });
          cap.textContent = f.title;
          edge.appendChild(cap);
        }
        cell.appendChild(edge);
        grid.appendChild(cell);
        pending.push({ img: img, url: f.cdn_url, ar: ar });
      });
      group.appendChild(grid);
      sheet.appendChild(group);
    });

    var backLink = root.querySelector('.back-link');
    if (backLink) root.insertBefore(sheet, backLink);
    else root.appendChild(sheet);

    // Measured once the sheet is laid out: every cell in the grid is the same
    // square, so the first one gives the size for all of them. 232px is the
    // widest cell the grid draws (3 columns at 749px), if it cannot be read.
    var firstFrame = sheet.querySelector('.lc-contact__frame');
    var cellPx = (firstFrame && firstFrame.getBoundingClientRect().width) || 232;
    var edge = thumbEdge(cellPx);
    for (var pi = 0; pi < pending.length; pi++) {
      try {
        pending[pi].img.src = thumbUrl(pending[pi].url, pending[pi].ar, edge);
      } catch (_e) {}
    }
  }

  // Fade each thumbnail in on load. Keyed to .js-fade, which only this script
  // adds, so with JS off nothing is hidden. Covers the Liquid sheet as well
  // as the fallback.
  function wireContactFade() {
    var sheet = document.querySelector('.lc-contact');
    if (!sheet) return;
    sheet.classList.add('js-fade');
    sheet.querySelectorAll('img').forEach(function (img) {
      function done() { img.classList.add('is-loaded'); }
      if (img.complete && img.naturalWidth) done();
      else {
        img.addEventListener('load', done, { once: true });
        img.addEventListener('error', done, { once: true });
      }
    });
  }

  // ----- Read-next corner badges --------------------------------------------
  function applyReadNextBadges() {
    document.querySelectorAll('.read-next .pane').forEach(function (pane) {
      var imgDiv = pane.querySelector('.img');
      if (!imgDiv) return;
      var tag = pane.querySelector('.meta .tag');
      if (!tag) return;
      var badge = document.createElement('span');
      badge.className = 'corner-badge';
      badge.textContent = tag.textContent.trim();
      imgDiv.appendChild(badge);
    });
  }

  // ----- Pull-quote injection -----------------------------------------------
  function injectPullQuotes(root) {
    var bodyEl = document.getElementById('articleBody');
    var raw = bodyEl ? bodyEl.getAttribute('data-pull-quotes') : null;
    if (!raw) return;
    try {
      var quotes = JSON.parse(raw);
      if (!Array.isArray(quotes)) return;
      quotes.forEach(function (pq) {
        if (!pq || !pq.text) return;
        var targetH2 = pq.after_section_anchor
          ? findH2BySlug(root, pq.after_section_anchor)
          : null;
        var pullEl = document.createElement('div');
        pullEl.className = 'pull';
        var pEl = document.createElement('p');
        pEl.textContent = pq.text;
        pullEl.appendChild(pEl);
        if (pq.attribution) {
          var cite = document.createElement('cite');
          cite.textContent = pq.attribution;
          pullEl.appendChild(cite);
        }
        if (targetH2) {
          // An archive chapter's h2 sits inside its .lc-archive-band, so the
          // quote goes into the band, at the end of that chapter.
          var parentEl = targetH2.parentNode;
          var insertBeforeEl = nextH2(targetH2);
          if (insertBeforeEl) parentEl.insertBefore(pullEl, insertBeforeEl);
          else parentEl.appendChild(pullEl);
        } else {
          root.appendChild(pullEl);
        }
      });
    } catch (_e) { /* graceful degradation */ }
  }

  function findH2BySlug(root, anchor) {
    var found = null;
    root.querySelectorAll('h2.h2-mark').forEach(function (h2) {
      var ttl = h2.querySelector('.ttl');
      if (!ttl) return;
      var slug = ttl.textContent.trim().toLowerCase()
        .replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
      if (slug === anchor || h2.id === anchor || h2.id === 'sec-' + slug) found = h2;
    });
    return found;
  }

  // The next chapter start among the h2's siblings. An archive band opens
  // with its chapter's h2, so the band itself is where that chapter starts.
  function nextH2(h2) {
    var sib = h2.nextElementSibling;
    while (sib) {
      if (sib.tagName && sib.tagName.toLowerCase() === 'h2') return sib;
      if (sib.classList && sib.classList.contains('lc-archive-band')) return sib;
      sib = sib.nextElementSibling;
    }
    return null;
  }

  function init() {
    var root = document.getElementById('articleBody');
    if (!root) return;
    buildContactSheet(root);
    wireContactFade();
    applyReadNextBadges();
    injectPullQuotes(root);
  }

  ready(function () {
    window.LCArticle.onReady(init);
  });
})();
